import { useState, useEffect } from 'react';
import { popupBannerAPI, IMAGE_BASE } from '../../services/api';
import { Save, Upload, Image as ImageIcon, Eye, EyeOff, Copy, Tag, X } from 'lucide-react';

export default function PopupBannerAdmin() {
    const [banner, setBanner] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState('');
    const [newImage, setNewImage] = useState(null);
    const [preview, setPreview] = useState(null);
    const [showPreviewModal, setShowPreviewModal] = useState(false);

    useEffect(() => {
        popupBannerAPI.get()
            .then(res => setBanner(res.data))
            .catch(console.error)
            .finally(() => setLoading(false));
    }, []);

    const handleChange = (field, value) => {
        setBanner(prev => ({ ...prev, [field]: value }));
    };

    const handleImageChange = (file) => {
        if (!file) return;
        setNewImage(file);
        setPreview(URL.createObjectURL(file));
    };

    const handleSave = async () => {
        setSaving(true);
        setMessage('');
        try {
            const formData = new FormData();
            formData.append('title', banner.title || '');
            formData.append('description', banner.description || '');
            formData.append('couponCode', banner.couponCode || '');
            formData.append('couponDescription', banner.couponDescription || '');
            formData.append('buttonText', banner.buttonText || '');
            formData.append('buttonLink', banner.buttonLink || '');
            formData.append('isActive', banner.isActive);
            formData.append('showOncePerSession', banner.showOncePerSession);
            formData.append('delaySeconds', banner.delaySeconds || 1);
            if (newImage) formData.append('image', newImage);
            const res = await popupBannerAPI.update(formData);
            setBanner(res.data);
            setNewImage(null);
            setPreview(null);
            setMessage('Kaydedildi.');
        } catch (e) {
            console.error(e);
            setMessage('Hata: Kaydedilemedi.');
        }
        setSaving(false);
    };

    if (loading) return <div className="loading"><div className="spinner"></div></div>;
    if (!banner) return <div className="admin-page"><p>Popup bulunamadı.</p></div>;

    const imgSrc = preview || (banner.image ? (banner.image.startsWith('http') ? banner.image : IMAGE_BASE + banner.image) : null);

    return (
        <div className="admin-page">
            <div className="admin-page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                    <h1>Popup Reklam</h1>
                    <p style={{ color: '#888', fontSize: '0.9rem', marginTop: '4px' }}>
                        Siteye ilk girişte açılan reklam/indirim popup'ını yönetin.
                    </p>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button
                        className="btn"
                        style={{ fontSize: '0.8rem', padding: '8px 16px' }}
                        onClick={() => setShowPreviewModal(true)}
                    >
                        <Eye size={14} /> Önizle
                    </button>
                    <span style={{
                        padding: '6px 14px',
                        borderRadius: '20px',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        background: banner.isActive ? '#ecfdf5' : '#fef2f2',
                        color: banner.isActive ? '#065f46' : '#991b1b',
                        border: `1px solid ${banner.isActive ? '#a7f3d0' : '#fecaca'}`
                    }}>
                        {banner.isActive ? 'Aktif' : 'Pasif'}
                    </span>
                </div>
            </div>

            {message && (
                <div className={`admin-alert ${message.startsWith('Hata') ? 'admin-alert-error' : 'admin-alert-success'}`}>
                    {message}
                </div>
            )}

            <div className="editorial-admin-layout">
                {/* Preview */}
                <div className="editorial-admin-preview">
                    <h3 style={{ marginBottom: '12px', fontSize: '0.9rem', color: '#6b7280' }}>Önizleme</h3>
                    <div className="popup-admin-preview-card">
                        {imgSrc && (
                            <div className="popup-admin-preview-img">
                                <img src={imgSrc} alt="Popup" />
                            </div>
                        )}
                        <div className="popup-admin-preview-content">
                            <h2>{banner.title}</h2>
                            {banner.description && <p>{banner.description}</p>}
                            {banner.couponCode && (
                                <div className="popup-admin-preview-coupon">
                                    {banner.couponDescription && (
                                        <span style={{ fontSize: '0.75rem', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <Tag size={11} /> {banner.couponDescription}
                                        </span>
                                    )}
                                    <div className="popup-admin-preview-coupon-box">
                                        <span>{banner.couponCode}</span>
                                        <Copy size={13} />
                                    </div>
                                </div>
                            )}
                            <span className="popup-admin-preview-cta">{banner.buttonText}</span>
                        </div>
                    </div>
                </div>

                {/* Form */}
                <div className="editorial-admin-form">
                    <div className="form-group">
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 700 }}>
                            <input
                                type="checkbox"
                                checked={banner.isActive}
                                onChange={(e) => handleChange('isActive', e.target.checked)}
                                style={{ width: '18px', height: '18px' }}
                            />
                            {banner.isActive ? <><Eye size={16} /> Popup Aktif</> : <><EyeOff size={16} /> Popup Pasif</>}
                        </label>
                    </div>

                    <div className="form-group">
                        <label>Görsel</label>
                        <div className="editorial-image-upload">
                            {imgSrc && <img src={imgSrc} alt="Popup" className="editorial-thumb" />}
                            <label className="homepage-cat-upload-btn" style={{ position: 'relative' }}>
                                <Upload size={14} /> Görsel Seç
                                <input type="file" accept="image/*" hidden onChange={(e) => handleImageChange(e.target.files[0])} />
                            </label>
                        </div>
                    </div>

                    <div className="form-group">
                        <label>Başlık</label>
                        <input type="text" value={banner.title || ''} onChange={(e) => handleChange('title', e.target.value)} placeholder="Hoşgeldiniz!" />
                    </div>

                    <div className="form-group">
                        <label>Açıklama</label>
                        <textarea rows={3} value={banner.description || ''} onChange={(e) => handleChange('description', e.target.value)} placeholder="İndirim açıklaması..." />
                    </div>

                    <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#92400e' }}>Kupon Kodu (opsiyonel)</span>
                        <div className="form-group" style={{ margin: 0 }}>
                            <label>Kupon Kodu</label>
                            <input type="text" value={banner.couponCode || ''} onChange={(e) => handleChange('couponCode', e.target.value.toUpperCase())} placeholder="HOŞGELDIN10" style={{ fontFamily: 'monospace', fontWeight: 700, letterSpacing: '1px' }} />
                        </div>
                        <div className="form-group" style={{ margin: 0 }}>
                            <label>Kupon Açıklaması</label>
                            <input type="text" value={banner.couponDescription || ''} onChange={(e) => handleChange('couponDescription', e.target.value)} placeholder="İlk alışverişte %10 indirim" />
                        </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                        <div className="form-group">
                            <label>Buton Metni</label>
                            <input type="text" value={banner.buttonText || ''} onChange={(e) => handleChange('buttonText', e.target.value)} placeholder="Alışverişe Başla" />
                        </div>
                        <div className="form-group">
                            <label>Buton Linki</label>
                            <input type="text" value={banner.buttonLink || ''} onChange={(e) => handleChange('buttonLink', e.target.value)} placeholder="/urunler" />
                        </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                        <div className="form-group">
                            <label>Gösterim Gecikmesi (saniye)</label>
                            <input type="number" min="0" max="30" value={banner.delaySeconds || 1} onChange={(e) => handleChange('delaySeconds', parseInt(e.target.value) || 0)} />
                        </div>
                        <div className="form-group">
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', marginTop: '22px' }}>
                                <input type="checkbox" checked={banner.showOncePerSession} onChange={(e) => handleChange('showOncePerSession', e.target.checked)} />
                                Oturum başına 1 kez göster
                            </label>
                        </div>
                    </div>

                    <button className="btn btn-primary" onClick={handleSave} disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: '6px', width: '100%', justifyContent: 'center', padding: '12px' }}>
                        <Save size={16} /> {saving ? 'Kaydediliyor...' : 'Kaydet'}
                    </button>
                </div>
            </div>

            {/* Full Preview Modal */}
            {showPreviewModal && (
                <div className="popup-overlay" onClick={() => setShowPreviewModal(false)} style={{ zIndex: 10000 }}>
                    <div className="popup-modal" onClick={e => e.stopPropagation()}>
                        <button className="popup-close" onClick={() => setShowPreviewModal(false)}><X size={20} /></button>
                        {imgSrc && (
                            <div className="popup-image"><img src={imgSrc} alt={banner.title} /></div>
                        )}
                        <div className="popup-content">
                            <h2 className="popup-title">{banner.title}</h2>
                            {banner.description && <p className="popup-desc">{banner.description}</p>}
                            {banner.couponCode && (
                                <div className="popup-coupon">
                                    {banner.couponDescription && (
                                        <span className="popup-coupon-label"><Tag size={13} /> {banner.couponDescription}</span>
                                    )}
                                    <div className="popup-coupon-box">
                                        <span className="popup-coupon-code">{banner.couponCode}</span>
                                        <button className="popup-coupon-copy"><Copy size={14} /> Kopyala</button>
                                    </div>
                                </div>
                            )}
                            <span className="popup-cta" style={{ display: 'inline-block', textAlign: 'center' }}>{banner.buttonText || 'Alışverişe Başla'}</span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
